/**
 * `storeCatalog.*` — Store Admin products/catalog (Commerce, V1.0.1 M2).
 *
 * A "product" is an ERP `item` (master: name/SKU/HSN/rate/tax) plus its
 * `store_listing` (merchandising: visibility, unit-of-sale, discount, MOQ,
 * images). `create` writes both atomically (ADR-0048); `list`/`get` return the
 * joined product with a *computed* selling price; `publish`/`archive`/`update`
 * operate on the listing.
 *
 * @see packages/domain/src/commerce/store · docs/ecom/v1.0.1-plan.md §5.2, §6
 */
import { z } from "zod";
import { uuid, paged, moneyPaise, country } from "../_common.js";

/**
 * Unit of sale — a single piece, or a fixed-ratio pack (garment size run).
 *
 * Since ADR-0051 this is a property of a **pricing entry**, not of the product:
 * one listing can offer a loose piece and several packs at once. The listing's
 * own `soldBy` mirrors `pricingEntries[0]`.
 */
export const soldBy = z.enum(["piece", "pack"]);
export type SoldBy = z.infer<typeof soldBy>;

export const visibility = z.enum(["draft", "published", "archived"]);
export type Visibility = z.infer<typeof visibility>;

/**
 * One way to buy a product — a loose piece, or a fixed pack (ADR-0051).
 *
 * Everything that varies between the ways a style is sold lives here: the
 * composition, the price, the discount and the minimum order. Only GST and the
 * measure unit stay on the product, because those are single columns the tax
 * invoice reads and the GST slab turns on per-piece value (`prd.md` §6.1 pt 5).
 *
 * `pieces` is derived from `sizeRatio`, not independently typed, so the two
 * cannot disagree. It travels on the wire so readers needn't re-parse.
 */
export const pricingEntry = z.object({
  /**
   * Which way this entry is sold. Optional on input so elements written before
   * ADR-0051 stay valid — the server backfills `"pack"`, which every one of
   * them was.
   *
   * Since spec 13 (pack-only pricing) the value `"piece"` is **rejected** at the
   * boundary (see the `superRefine` below): garments wholesale in packs only.
   * The enum type is left intact so `resolvePricingEntries`' legacy-read branch
   * still type-checks; the full type-level removal lands with the product
   * pricing rework (spec 13, S4).
   */
  kind: soldBy.optional(),
  /**
   * The library pack this entry prices against (spec 13, R4). Optional at the
   * contract layer during the S2→S4 transition; the product rework makes it the
   * source of composition/pieces and the required key of every entry.
   */
  packId: uuid.optional(),
  /** Operator-facing name — "Piece", "Pack of 60". */
  label: z.string().trim().min(1).max(24),
  /**
   * `SIZE:N`, and its reading follows `kind`: for a pack the composition of one
   * bundle (`S:1,M:2,L:2,XL:1`), for a piece the depth carried in each size
   * (`S:10,M:20,L:20`).
   *
   * **Optional** — the size grid carries no validation, so an entry may be
   * priced without one. `pieces` is then 0 and no per-pack arithmetic is
   * possible, which is the operator's call to make.
   */
  sizeRatio: z.string().trim().max(64),
  /** Pieces this entry covers — a pack's composition total, or 1 for a piece. */
  pieces: z.number().int().min(0).max(1000),
  /**
   * What this entry costs — the bundle for a pack, one garment for a piece.
   * Integer paise (stringified bigint).
   *
   * Stored rather than re-derived from the per-piece rate, because dividing and
   * multiplying back loses paise on any price that doesn't divide evenly:
   * ₹5,000 over 60 comes back ₹4,999.80. `items.default_rate_paise` remains the
   * per-piece figure invoice line rates and the GST slab read; this is the price
   * the entry is *quoted and ordered* at (`store_order_lines.qty` is in the unit
   * of sale, `captured_price_paise` per that unit).
   *
   * Optional so pre-ADR rows stay valid — the server fills it from
   * `basePricePaise × pieces`.
   */
  pricePaise: moneyPaise.optional(),
  /** Discount off this entry's price, basis points. Per entry since ADR-0051. */
  discountBps: z.number().int().min(0).max(10_000).optional(),
  /**
   * Minimum order for this entry — packs for a pack, pieces for a piece.
   *
   * Per entry because MOQ is denominated in the unit of sale (`prd.md` §6.1
   * pt 4), and a listing offering several ways to buy can't say which one a
   * single product-level minimum means. `store_listings.moq` keeps being
   * written, mirrored from `pricingEntries[0]`.
   */
  moq: z.number().int().min(1).max(1_000_000).optional(),
  /**
   * How many of this pack the product has — recorded on the listing, **not**
   * the stock ledger.
   *
   * Deliberately not a stock balance: `stock_balances` is denominated in
   * pieces and has no pack dimension, so it cannot answer "how many of this
   * pack are left". Since 2026-08-05 the storefront reads this as the buyer's
   * quantity cap (stepper max — spec 13 R4a amendment). Pieces it covers
   * (`packsOnHand × pieces`) is derived at render, never stored — same rule
   * as the per-piece rate (spec 13, R6).
   *
   * **Since 2026-08-07 fulfilling an order deducts it** (ADR-0056), so it is
   * no longer purely operator-maintained. Which is why the floor is `0`, not
   * `1`: `0` is the meaningful end state *none left*, and the storefront
   * already renders it unavailable (`packsOnHand < moq`). Clearing the field
   * instead would read as **untracked** and lift the cap to 9,999 — the
   * opposite of sold out. Note this schema validates outputs too, so the floor
   * has to admit every value the deduction can store.
   *
   * Named apart from the cart's `packQty` on purpose: that one is how many
   * packs a *buyer* is ordering, this one is how many the *seller* holds.
   */
  packsOnHand: z.number().int().min(0).max(1_000_000).optional(),
  /**
   * The bundle's colour(s), sourced from the product's Color variant axis
   * (issue #235). An overlay on the entry, not on the library pack — packs
   * stay colour-agnostic and reusable across products.
   *
   * Mode/colour coherence (single ⇒ exactly one colour) is enforced in the
   * authoring dialog only, NOT here — this schema validates tRPC outputs too,
   * and a cross-field refine on this fragment has broken saves twice before
   * (PR #219, ADR-0056 consequences). Absent on every pre-#235 entry.
   */
  colorSpec: z
    .object({
      mode: z.enum(["mixed", "single"]),
      colors: z.array(z.string().trim().min(1).max(48)).min(1).max(24),
    })
    .optional(),
  /**
   * The per-variant breakdown behind a bundle composed from generated
   * variants (issue #235 follow-up): which combinations, how many of each.
   * The pack's size-level composition is the per-size sum of these; this
   * overlay keeps the finer grain so the card can say "S·Black ×2". Same
   * no-cross-field-refine discipline as `colorSpec` — coherence with the
   * pack composition is the authoring dialog's job.
   */
  variantComposition: z
    .array(
      z.object({
        attributeValues: z.record(z.string().trim().min(1).max(48), z.string().trim().min(1).max(48)),
        qty: z.number().int().min(1).max(1000),
      }),
    )
    .max(100)
    .optional(),
}).superRefine((entry, ctx) => {
  // Pack-only pricing (spec 13, R4/AC4): a piece entry is no longer a valid way
  // to buy. Rejected here so the error names the `kind` field on the offending
  // entry rather than surfacing as an opaque failure downstream.
  if (entry.kind === "piece") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["kind"],
      message: 'Pricing is per pack — the "piece" option was removed.',
    });
  }
  // R4: "each reference a library pack." Any entry carrying a real price must
  // name the pack it prices — the server floor, so a direct API/AI-tool call
  // can't write a priced entry with no pack link (the web dialog always sends
  // one). A priceless placeholder entry is still allowed (drafts, R5).
  const priced = entry.pricePaise !== undefined && entry.pricePaise !== "0";
  if (priced && !entry.packId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["packId"],
      message: "Pick a pack — every price is per pack.",
    });
  }
  // R4a as amended: `moq ≤ packsOnHand` is deliberately NOT enforced here.
  // This schema validates tRPC OUTPUTS too (`productRow` → get/list) and the
  // wholesale-resent `updateInput.pricingEntries`, so a refine would reject
  // *stored* contradictory rows — breaking the products list/detail on read
  // and blocking unrelated edits (PR #219 review, C1/H1). Persisted
  // contradictory entries are legitimate: the storefront renders them
  // UNAVAILABLE (`packUnorderable`). The invariant is enforced at authoring
  // time in `pricing-entry-dialog.tsx`, the only place entries are written.
});
export type PricingEntry = z.infer<typeof pricingEntry>;

/**
 * Capped at 12. A style with more than a dozen ways to buy it is a catalog
 * modelling problem, and an unbounded array is an unbounded jsonb column.
 */
export const pricingEntries = z.array(pricingEntry).max(12);

// ── variant matrix (attribute axes + generated rows) ──────────────
/**
 * The matrix's axis definitions, ordered. Separate from `attributes` (flat
 * single-value merchandising copy) — these exist only to combine into
 * variants. Capped at 4 axes: 4 × ~6 values is already beyond any real
 * garment matrix, and the variants cap below is the hard stop.
 */
export const variantAttributes = z
  .array(
    z.object({
      name: z.string().trim().min(1).max(48),
      values: z.array(z.string().trim().min(1).max(48)).max(24),
    }),
  )
  .max(4);

/**
 * One sellable combination. Generated client-side (the cartesian product is
 * form work); the server validates shape, caps the count, and enforces SKU
 * uniqueness across the tenant. 100 rows is a hard ceiling — far above a
 * legitimate matrix, low enough that a typo can't submit thousands.
 */
export const variant = z.object({
  attributeValues: z.record(z.string().trim().min(1).max(48), z.string().trim().min(1).max(48)),
  sku: z.string().trim().min(1).max(64).optional(),
  barcode: z.string().trim().min(1).max(32).optional(),
  weightGrams: z.number().int().min(0).max(10_000_000).optional(),
  // No quantity: stock on hand is derived from the bundles that contain the
  // variant (`variantQtyFromBundles`, ADR-0061), never sent by the client.
});
export const variants = z.array(variant).max(100);

// ── create (item + listing, atomic) ───────────────────────────────
export const createInput = z.object({
  // item master
  name: z.string().trim().min(1, "Name is required").max(255),
  sku: z.string().trim().min(1).max(32).optional(),
  hsn: z
    .string()
    .trim()
    .regex(/^\d{4,8}$/, "4-8 digit HSN/SAC")
    .optional(),
  unit: z.string().trim().min(1).max(16).optional(),
  category: z.string().trim().min(1).max(48).optional(),
  /**
   * Second taxonomy level, free text like its parent. Optional where `category`
   * is required: plenty of catalogues are one level deep, and forcing a child
   * would have operators inventing filler terms.
   *
   * Not validated against `category` here. The pairing is asserted by the row
   * being saved — that is what makes a sub-category belong to a category in the
   * first place, so there is no server-side set to check membership against.
   */
  subCategory: z.string().trim().min(1).max(48).optional(),
  /** Base rate per piece, integer paise (stringified bigint). */
  basePricePaise: moneyPaise,
  /** GST rate in basis points (500 = 5%). */
  taxRateBps: z.number().int().min(0).max(10_000).optional(),
  // listing merchandising
  soldBy: soldBy.default("piece"),
  piecesPerPack: z.number().int().min(1).max(1000).optional(),
  sizeRatio: z.string().trim().min(1).max(64).optional(),
  /**
   * Every way to buy this product. `pricingEntries[0]` is mirrored into
   * `soldBy` / `sizeRatio` / `piecesPerPack` / `discountBps` / `moq`
   * server-side, so a caller sending only those stays valid — the array is
   * additive, not a replacement.
   */
  pricingEntries: pricingEntries.optional(),
  /** Fallback when no entries are sent; otherwise mirrored from the primary. */
  discountBps: z.number().int().min(0).max(10_000).default(0),
  moq: z.number().int().min(1).max(1_000_000).optional(),
  shortDesc: z.string().trim().min(1).max(500).optional(),
  longDesc: z.string().trim().min(1).max(4000).optional(),
  brand: z.string().trim().min(1).max(96).optional(),
  /**
   * Where it was made. Optional: a product is valid without one, and the form
   * defaults the control to India rather than the server defaulting the column
   * — an absent value means nobody has said, not "made in India".
   */
  countryOfOrigin: country.optional(),
  description: z.string().trim().min(1).max(500).optional(),
  attributes: z.record(z.string(), z.string().trim().max(120)).optional(),
  /** The variant matrix's axes. @see the `variantAttributes` fragment. */
  variantAttributes: variantAttributes.optional(),
  /** One row per attribute combination. Generated client-side, validated here. */
  variants: variants.optional(),
  /**
   * The collection this product belongs to, written as a real
   * `store_collection_listings` row in the same transaction. Single-select here
   * (the wireframe draws one picker); the junction is N:N, so membership can
   * also be curated from the collection side. `null` clears it.
   */
  collectionId: uuid.nullable().optional(),
  images: z.array(z.string().trim().min(1).max(2048)).max(8).optional(),
  /**
   * Opening stock for the new product, posted in the same transaction as the
   * item + listing. Optional: a product is valid with no stock on hand, and
   * only a go-live/migration flow states an opening figure. Valued at the
   * product's own base price — there is no separate cost field on this form.
   *
   * Create-only by design. An opening balance is a one-time starting point;
   * later movement belongs to the Inventory module's adjust/transfer flows.
   */
  openingStock: z
    .object({
      warehouseId: uuid,
      quantity: z.number().int().positive("Quantity must be positive"),
    })
    .optional(),
});
export const createOutput = z.object({ itemId: uuid, listingId: uuid });

// ── the joined product row ────────────────────────────────────────
export const productRow = z.object({
  listingId: uuid,
  itemId: uuid,
  name: z.string(),
  sku: z.string().nullable(),
  hsn: z.string().nullable(),
  unit: z.string(),
  category: z.string().nullable(),
  /** Null on products saved before the field existed, and wherever left blank. */
  subCategory: z.string().nullable(),
  /** Null on products that predate the column and wherever nobody has said. */
  countryOfOrigin: z.string().nullable(),
  visibility,
  soldBy,
  piecesPerPack: z.number().int().nullable(),
  sizeRatio: z.string().nullable(),
  /** Every way to buy it; `[0]` is the primary mirrored into the fields above. */
  pricingEntries: pricingEntries,
  /** Item base rate (per piece), stringified paise. */
  basePricePaise: z.string().nullable(),
  discountBps: z.number().int(),
  /** Computed: base × (1 − discount). Per piece, stringified paise. */
  sellingPricePaise: z.string().nullable(),
  taxRateBps: z.number().int().nullable(),
  moq: z.number().int().nullable(),
  shortDesc: z.string().nullable(),
  longDesc: z.string().nullable(),
  brand: z.string().nullable(),
  description: z.string().nullable(),
  attributes: z.record(z.string(), z.string()),
  /** The variant matrix's axes; `[]` when the product has none. */
  variantAttributes: variantAttributes,
  /** Collection membership, read from the junction. */
  collectionId: uuid.nullable(),
  collectionName: z.string().nullable(),
  images: z.array(z.string()),
  sort: z.number().int(),
  createdAt: z.string().datetime(),
});

// ── list ──────────────────────────────────────────────────────────
export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  q: z.string().optional(),
  /**
   * The last two are **availability** filters, not `visibility` states: they
   * narrow the same non-archived set the `all` tab lists, so they overlap
   * `published`/`draft` rather than partitioning alongside them (spec 13 R4c).
   */
  tab: z
    .enum(["all", "published", "draft", "archived", "out-of-stock", "below-minimum"])
    .default("all"),
});
export const listOutput = paged(productRow);

// ── counts ────────────────────────────────────────────────────────
/**
 * Per-visibility totals for the list's tab badges. A separate procedure
 * rather than a field on `listOutput` so the SPA can refresh the badges
 * after a publish/archive without re-paginating — one cheap aggregate.
 *
 * `all` counts the NON-ARCHIVED catalog (published + draft), because the
 * `all` tab's own query excludes archived. A table-wide total here would
 * show a badge that disagrees with the rows the tab renders.
 */
/**
 * Tab badge totals. Keyed by the **tab value**, not by a prettier name — the
 * list renders `counts[tab]`, so a mismatch is a silently blank badge.
 *
 * The two availability figures overlap `published`/`draft`, so these six do not
 * sum to anything meaningful (spec 13 R4c).
 */
export const countsOutput = z.object({
  all: z.number().int().nonnegative(),
  published: z.number().int().nonnegative(),
  draft: z.number().int().nonnegative(),
  archived: z.number().int().nonnegative(),
  "out-of-stock": z.number().int().nonnegative(),
  "below-minimum": z.number().int().nonnegative(),
});

// ── product image upload ──────────────────────────────────────────
/**
 * Phase 1 of a direct-to-S3 product image upload, mirroring
 * `identity.requestAvatarUpload`. There is deliberately **no phase-2
 * confirm**: an avatar flips a DB column, whereas a product image's record
 * *is* its URL inside `store_listings.images`, which persists when the form
 * is saved. Returning `publicUrl` here is what removes the extra round-trip.
 *
 * Keyed by company rather than listing, so the New-product form — which has
 * no listing id yet — can upload before the product exists.
 */
export const requestImageUploadInput = z.object({
  /** MIME the SPA will send. The signed URL pins it — mismatched PUTs fail. */
  contentType: z.enum(["image/jpeg", "image/png", "image/webp"]),
  /** Bytes the client intends to upload — capped at 5 MiB server-side. */
  size: z
    .number()
    .int()
    .positive()
    .max(5 * 1024 * 1024),
});
export const requestImageUploadOutput = z.object({
  uploadUrl: z.string(),
  objectKey: z.string(),
  /** Null when no public host is configured — the SPA then can't show it. */
  publicUrl: z.string().nullable(),
});

// ── catalog facets (picker vocabulary) ────────────────────────────
/**
 * Distinct `items.category` values already in use by the tenant. Free text with
 * no master table, so the product form's picker is built from what the item
 * catalogue actually contains.
 *
 * Collections are deliberately absent: `collection` only exists as a key in
 * `store_listings.attributes`, and its vocabulary belongs to the Collections
 * module (`store_collections`, M6) rather than to this lookup.
 */
export const facetsOutput = z.object({
  categories: z.array(z.string()),
  /**
   * Sub-categories grouped under the category they were saved against. The
   * whole tree ships in one response rather than behind a per-parent query:
   * the form re-filters the child picker on every parent change, and a round
   * trip there would put a spinner inside a form field.
   *
   * A category with no sub-categories yet is simply absent from the map — the
   * form reads a missing key as an empty list, which is what it means. Names
   * may repeat across parents; the pairing is per product row, not global.
   */
  subCategoriesByCategory: z.record(z.string(), z.array(z.string())),
});

// ── product QR permalink (spec 52, ADR-0066) ──────────────────────
/**
 * Mint (or return) the listing's QR permalink. Idempotent — a printed QR must
 * never change under the operator, so every call returns the same token.
 */
export const ensureQrTokenInput = z.object({ listingId: uuid });
export const ensureQrTokenOutput = z.object({
  /** Opaque base64url token — the whole public identity of the QR. */
  token: z.string(),
  /**
   * The full URL the QR encodes: `{FLINT_STORE_ORIGIN}/q/{token}`. Built
   * server-side (the `buildPaymentLink` precedent) so the SPA never guesses
   * the storefront origin.
   */
  qrUrl: z.string(),
});

// ── get one ───────────────────────────────────────────────────────
export const getInput = z.object({ listingId: uuid });
/**
 * `get` carries the variant rows; `list` does not — the table never shows
 * them, and joining the variants of 25 listings per page for nothing is the
 * kind of weight the cursor pagination exists to avoid.
 */
export const getOutput = productRow.extend({
  variants: z.array(
    z.object({
      attributeValues: z.record(z.string(), z.string()),
      sku: z.string().nullable(),
      barcode: z.string().nullable(),
      weightGrams: z.number().int().nullable(),
      // Quantity is derived from `pricingEntries` above, which this same
      // payload already carries — not stored, not returned (ADR-0061).
    }),
  ),
  /**
   * The item's piece-ledger balances per warehouse (available pcs) — the
   * Inventory card on the product detail page (UX-review finding 4 display
   * slice; ADR-0065 makes these figures follow store sales). Empty when the
   * item is untracked or holds no stock.
   */
  warehouseStock: z.array(
    z.object({
      warehouseId: uuid,
      warehouseName: z.string(),
      pieces: z.number().int(),
    }),
  ),
  /** Σ over `warehouseStock` — server-summed so the two can't drift. */
  stockPiecesTotal: z.number().int(),
  /**
   * The listing's QR permalink token, when one was ever minted (spec 52).
   * Null until the first "Generate QR code" click.
   */
  qrToken: z.string().nullable(),
  /**
   * The full permalink URL (`{FLINT_STORE_ORIGIN}/q/{token}`), server-built
   * like `ensureQrTokenOutput.qrUrl` so the QR card can render straight off
   * the `get` payload. Null exactly when `qrToken` is.
   */
  qrUrl: z.string().nullable(),
});

// ── publish / unpublish / archive (visibility transitions) ────────
export const publishInput = z.object({ listingId: uuid });
export const publishOutput = z.object({ listingId: uuid, visibility });
export const archiveInput = z.object({ listingId: uuid });
export const archiveOutput = z.object({ listingId: uuid, visibility });
/** Pull a published listing back to draft — the reverse of `publish`. @see ADR-0050 */
export const unpublishInput = z.object({ listingId: uuid });
export const unpublishOutput = z.object({ listingId: uuid, visibility });

// ── update listing merchandising (never touches item master) ──────
export const updateInput = z.object({
  listingId: uuid,
  // item master (SKU stays immutable — it may key an order)
  name: z.string().trim().min(1).max(255).optional(),
  hsn: z
    .string()
    .trim()
    .regex(/^\d{4,8}$/, "4-8 digit HSN/SAC")
    .nullable()
    .optional(),
  unit: z.string().trim().min(1).max(16).optional(),
  category: z.string().trim().min(1).max(48).nullable().optional(),
  /** `null` clears it — the operator moved the product back to one level. */
  subCategory: z.string().trim().min(1).max(48).nullable().optional(),
  basePricePaise: moneyPaise.optional(),
  taxRateBps: z.number().int().min(0).max(10_000).nullable().optional(),
  // listing merchandising
  discountBps: z.number().int().min(0).max(10_000).optional(),
  moq: z.number().int().min(1).max(1_000_000).nullable().optional(),
  shortDesc: z.string().trim().max(500).nullable().optional(),
  longDesc: z.string().trim().max(4000).nullable().optional(),
  brand: z.string().trim().max(96).nullable().optional(),
  /** `null` clears it — back to "nobody has said". */
  countryOfOrigin: country.nullable().optional(),
  description: z.string().trim().max(500).nullable().optional(),
  attributes: z.record(z.string(), z.string().trim().max(120)).optional(),
  /** Replaces the axes wholesale when present; omitted leaves them alone. */
  variantAttributes: variantAttributes.optional(),
  /**
   * Replaces the variant set diff-wise when present. Omitted = untouched;
   * `[]` = delete every variant. The distinction matters — a caller that
   * doesn't know about variants must not wipe them.
   */
  variants: variants.optional(),
  /**
   * The collection this product belongs to, written as a real
   * `store_collection_listings` row in the same transaction. Single-select here
   * (the wireframe draws one picker); the junction is N:N, so membership can
   * also be curated from the collection side. `null` clears it.
   */
  collectionId: uuid.nullable().optional(),
  soldBy: soldBy.optional(),
  piecesPerPack: z.number().int().min(1).max(1000).nullable().optional(),
  sizeRatio: z.string().trim().max(64).nullable().optional(),
  pricingEntries: pricingEntries.optional(),
  images: z.array(z.string().trim().min(1).max(2048)).max(8).optional(),
});
export const updateOutput = z.object({ listingId: uuid });

// ── derived TS types ──────────────────────────────────────────────
export type CreateInput = z.infer<typeof createInput>;
export type CreateOutput = z.infer<typeof createOutput>;
export type ListInput = z.infer<typeof listInput>;
export type ListOutput = z.infer<typeof listOutput>;
export type GetInput = z.infer<typeof getInput>;
export type ProductRow = z.infer<typeof productRow>;
export type FacetsOutput = z.infer<typeof facetsOutput>;
export type PublishInput = z.infer<typeof publishInput>;
export type ArchiveInput = z.infer<typeof archiveInput>;
export type UnpublishInput = z.infer<typeof unpublishInput>;
export type UpdateInput = z.infer<typeof updateInput>;
export type RequestImageUploadInput = z.infer<typeof requestImageUploadInput>;
export type RequestImageUploadOutput = z.infer<typeof requestImageUploadOutput>;
export type EnsureQrTokenInput = z.infer<typeof ensureQrTokenInput>;
export type EnsureQrTokenOutput = z.infer<typeof ensureQrTokenOutput>;
